/**
 * @file 学びメモの一覧と紐づけの表示に使う純関数である。
 *
 * @what: 一覧の行の更新日と本文の先頭、一覧の並べ替え、紐づけの同一の判定と付け外し、本ごとの一覧を持つ。
 * @why: 本を読む画面のメモ (StudyNotePane) と学びメモの画面 (StudyNotesRoute) が同じ規則で出す。
 * @failure: 純関数で、失敗しない。読めない日付は空の文字列にする。
 */

import type { StudyNote, StudyNoteRef } from "../api/notes";
import type { StudySource } from "../api/sources";

/** 一覧の行に出す更新日 (今年なら月日だけ)。 */
export function formatNoteDate(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const md = `${d.getMonth() + 1}/${d.getDate()}`;
  return d.getFullYear() === now.getFullYear() ? md : `${d.getFullYear()}/${md}`;
}

/** 学びメモの一覧の並び (更新が新しい順 / 作成が新しい順 / 題名順)。 */
export type NoteSort = "updated" | "created" | "title";

export const NOTE_SORTS: readonly { sort: NoteSort; label: string }[] = [
  { sort: "updated", label: "更新が新しい順" },
  { sort: "created", label: "作成が新しい順" },
  { sort: "title", label: "題名順" },
];

const titleCollator = new Intl.Collator("ja", { numeric: true });

/**
 * 一覧を並べ替えた新しい配列を返す。日付の並びは ISO 8601 の文字列の比較で足り、同じ日時と同じ題名は
 * 元の並び (サーバの更新順) を保つ。
 */
export function sortNotes(notes: readonly StudyNote[], sort: NoteSort): StudyNote[] {
  const desc = (a: string, b: string) => (a < b ? 1 : a > b ? -1 : 0);
  return [...notes].sort((a, b) => {
    switch (sort) {
      case "created":
        return desc(a.created_at, b.created_at);
      case "title":
        return titleCollator.compare(a.title, b.title);
      default:
        return desc(a.updated_at, b.updated_at);
    }
  });
}

/** 一覧に出す本文の長さである。 */
const PREVIEW_MAX_CHARS = 60;

/**
 * 一覧に出す本文の先頭である。画像の参照・コードの欄・表の区切りと Markdown の行頭の記号を落とし、
 * 行をつなぐ。
 */
export function notePreview(body: string | undefined): string {
  const text = (body ?? "")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    // コードの欄 (mermaid の図を含む) は文ではないので、閉じていない欄も末尾まで落とす
    .replace(/^ {0,3}(`{3,}|~{3,})[^\n]*\n[\s\S]*?(?:^ {0,3}\1[^\n]*$|(?![\s\S]))/gm, "")
    .split("\n")
    // 表の区切りの行 (| --- | :-: |) は落とし、セルの区切りの | は空白にする
    .filter((line) => !/^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/.test(line))
    .map((line) =>
      line
        .replace(/^\s*(#{1,6}|[-*+]|\d+\.|>)\s+/, "")
        .replace(/\s*\|\s*/g, " ")
        .trim(),
    )
    .filter((line) => line !== "")
    .join(" ");
  return text.length > PREVIEW_MAX_CHARS ? `${text.slice(0, PREVIEW_MAX_CHARS)}…` : text;
}

export function sameNoteRef(a: StudyNoteRef, b: StudyNoteRef): boolean {
  return a.kind === b.kind && a.source_id === b.source_id && (a.page ?? 0) === (b.page ?? 0);
}

/** refs に target があれば外し、無ければ足した並びを返す。 */
export function toggledNoteRefs(
  refs: readonly StudyNoteRef[],
  target: StudyNoteRef,
): StudyNoteRef[] {
  return refs.some((r) => sameNoteRef(r, target))
    ? refs.filter((r) => !sameNoteRef(r, target))
    : [...refs, target];
}

/**
 * 紐づけの資料を本にまとめた id である。別冊 (companion_of を持つ資料) は本体の本にまとめる。
 * ※なぜなら問題編と解答編のメモを、1冊の本のメモとして探せるようにするためである
 *   (本だなの「さいきん書いたメモ」も同じ規則でまとめる)。
 */
export function noteBookId(
  sourceId: string,
  sourcesById: ReadonlyMap<string, StudySource>,
): string {
  return sourcesById.get(sourceId)?.companion_of ?? sourceId;
}

/** メモが紐づく本の id (重複なし) である。 */
export function noteBookIds(
  note: StudyNote,
  sourcesById: ReadonlyMap<string, StudySource>,
): Set<string> {
  return new Set(
    (note.refs ?? [])
      .filter((r) => r.kind === "source")
      .map((r) => noteBookId(r.source_id, sourcesById)),
  );
}

export interface NoteBookSummary {
  bookId: string;
  /** 本の名前である。本だなから消えた本は id のまま出す。 */
  title: string;
  count: number;
  /** その本に紐づくメモを、最後に作った日時 (ISO)。 */
  lastCreated: string;
}

/**
 * メモから本ごとの一覧を作る。その本のメモを最後に作った日時の新しい順で、同じなら名前の順に並べる。
 *
 * @why: 並びは「最近メモを作った本」が上に来るようにする (代表の指示、2026-10-29)。更新日時で並べると、
 *   古いメモを書き直しただけで本の順が入れ替わる。
 */
export function summarizeNoteBooks(
  notes: readonly StudyNote[],
  sourcesById: ReadonlyMap<string, StudySource>,
): NoteBookSummary[] {
  const byBook = new Map<string, NoteBookSummary>();
  for (const note of notes) {
    for (const bookId of noteBookIds(note, sourcesById)) {
      const prev = byBook.get(bookId);
      if (prev === undefined) {
        byBook.set(bookId, {
          bookId,
          title: sourcesById.get(bookId)?.title ?? bookId,
          count: 1,
          lastCreated: note.created_at,
        });
      } else {
        prev.count += 1;
        if (note.created_at > prev.lastCreated) prev.lastCreated = note.created_at;
      }
    }
  }
  return [...byBook.values()].sort((a, b) =>
    a.lastCreated === b.lastCreated
      ? a.title.localeCompare(b.title, "ja")
      : a.lastCreated < b.lastCreated
        ? 1
        : -1,
  );
}

/**
 * 一覧の行に表紙を出す資料と、その本体である。メモの最初の紐づけの資料を使う。
 * 別冊なら本体も返し、表紙は本体の表紙に「解答編」を重ねた形になる (SourceCover の parent)。
 * 紐づけが無い・本だなから消えた資料なら null を返す。
 */
export function noteCoverSource(
  note: StudyNote,
  sourcesById: ReadonlyMap<string, StudySource>,
): { source: StudySource; parent?: StudySource } | null {
  const ref = (note.refs ?? []).find((r) => r.kind === "source");
  const source = ref ? sourcesById.get(ref.source_id) : undefined;
  if (source === undefined) return null;
  const parent = source.companion_of ? sourcesById.get(source.companion_of) : undefined;
  return parent ? { source, parent } : { source };
}
